import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trophy, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const RED = "#E30613";

function monthBounds(offset = 0) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmt(first), end: fmt(last) };
}

export default function Reports() {
  const [data, setData] = useState(null);
  const [fu, setFu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(monthBounds(0));
  const [exporting, setExporting] = useState(false);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "this_month") setRange(monthBounds(0));
    else if (p === "last_month") setRange(monthBounds(-1));
    else if (p === "all") setRange({ start: "", end: "" });
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    Promise.all([
      api.get("/admin/reports", { params }).then(({ data }) => setData(data)),
      api.get("/admin/follow-ups/reports").then(({ data }) => setFu(data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const res = await api.get("/admin/reports/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `sgbinoxe-report-${range.start || "all"}-to-${range.end || "now"}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report exported");
    } catch (e) { toast.error("Export failed"); } finally { setExporting(false); }
  };

  if (!data) return <DashboardLayout title="Reports"><div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div></DashboardLayout>;

  const inputCls = "h-10 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 text-sm text-white [color-scheme:dark] focus-visible:border-[#E30613] focus-visible:outline-none";

  return (
    <DashboardLayout title="Reports">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Analytics</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Platform reports</h2>
          <p className="mt-1 text-sm text-neutral-500">Certificates, activations, credits and top installers.</p>
        </div>
        <button onClick={exportCsv} disabled={exporting} className="inline-flex items-center gap-2 self-start rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60 lg:self-auto" data-testid="export-report-btn">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV</button>
      </div>

      <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-white/[0.06] bg-[#141414] p-4 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap gap-2">
          {[{ k: "this_month", l: "This Month" }, { k: "last_month", l: "Last Month" }, { k: "custom", l: "Custom" }, { k: "all", l: "All Time" }].map(({ k, l }) => (
            <button key={k} onClick={() => applyPreset(k)} className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${preset === k ? "border-[#E30613] bg-[#E30613]/15 text-white" : "border-white/[0.1] text-neutral-400 hover:bg-white/[0.05]"}`} data-testid={`preset-${k}`}>{l}</button>
          ))}
        </div>
        {(preset === "custom" || range.start) && (
          <div className="flex items-center gap-2 sm:ml-auto">
            <input type="date" value={range.start} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, start: e.target.value })); }} className={inputCls} data-testid="report-start" />
            <span className="text-neutral-500">to</span>
            <input type="date" value={range.end} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, end: e.target.value })); }} className={inputCls} data-testid="report-end" />
          </div>
        )}
      </div>

      {loading && <div className="mb-4 flex items-center gap-2 text-xs text-neutral-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…</div>}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-white/[0.06] bg-[#141414] p-5" data-testid="stat-certs"><p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">Certificates</p><p className="mt-2 font-display text-3xl font-semibold text-white">{data.certificates_total}</p></div>
        <div className="rounded-xl border border-white/[0.06] bg-[#141414] p-5" data-testid="stat-activations"><p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">Activations</p><p className="mt-2 font-display text-3xl font-semibold text-white">{data.activations_total}</p></div>
        <div className="rounded-xl border border-white/[0.06] bg-[#141414] p-5"><p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">Credits Used</p><p className="mt-2 font-display text-3xl font-semibold text-white">{data.credits_used}</p></div>
        <div className="rounded-xl border border-white/[0.06] bg-[#141414] p-5"><p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">Credits Remaining</p><p className="mt-2 font-display text-3xl font-semibold text-[#FF1C2A]">{data.credits_remaining}</p></div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="chart-certs">
          <h3 className="mb-5 font-display text-base font-medium text-white">Certificates Per Month</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.certificates_per_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: "#737373", fontSize: 11 }} />
              <YAxis tick={{ fill: "#737373", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
              <Bar dataKey="count" fill={RED} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="chart-activations">
          <h3 className="mb-5 font-display text-base font-medium text-white">Bottle Activations Per Month</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.activations_per_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: "#737373", fontSize: 11 }} />
              <YAxis tick={{ fill: "#737373", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
              <Line type="monotone" dataKey="count" stroke={RED} strokeWidth={2} dot={{ fill: RED }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="top-installers">
        <div className="mb-5 flex items-center gap-2"><Trophy className="h-4 w-4 text-[#FF1C2A]" /><h3 className="font-display text-base font-medium text-white">Top Installers</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500"><th className="py-3 pr-3">#</th><th className="py-3 pr-3">Business</th><th className="py-3 pr-3">Certificates</th><th className="py-3 pr-3">Activations</th><th className="py-3">Credits Used</th></tr></thead>
            <tbody>
              {data.top_installers.map((t, i) => (
                <tr key={i} className="border-b border-white/[0.04]">
                  <td className="py-3 pr-3 text-neutral-500">{i + 1}</td>
                  <td className="py-3 pr-3 font-medium text-white">{t.business_name}</td>
                  <td className="py-3 pr-3 text-neutral-300">{t.certificates_generated}</td>
                  <td className="py-3 pr-3 text-neutral-300">{t.bottle_activations}</td>
                  <td className="py-3 text-neutral-300">{t.credits_used}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.packages && (
        <div className="mt-6 rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="package-breakdown">
          <h3 className="mb-5 font-display text-base font-medium text-white">Installation Packages</h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {data.packages.map((p) => (
              <div key={p.package} className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{p.package}</p>
                <p className="mt-2 font-display text-3xl font-semibold text-white">{p.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {fu && (
        <>
          <h3 className="mb-4 mt-10 font-display text-base font-medium text-white">Customer Follow-Ups</h3>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5" data-testid="fu-report-stats">
            <StatBox label="Completed" value={fu.completed} />
            <StatBox label="Outstanding" value={fu.outstanding} accent />
            <StatBox label="Skipped" value={fu.skipped} />
            <StatBox label="Unable to Contact" value={fu.unable} />
            <StatBox label="Avg Completion (days)" value={fu.avg_completion_days} />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="fu-most-completed">
              <div className="mb-5 flex items-center gap-2"><Trophy className="h-4 w-4 text-[#FF1C2A]" /><h3 className="font-display text-base font-medium text-white">Most Follow-Ups Completed</h3></div>
              {fu.most_completed.length === 0 ? <p className="py-6 text-center text-sm text-neutral-500">No data yet.</p> : (
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500"><th className="py-3 pr-3">#</th><th className="py-3 pr-3">Business</th><th className="py-3">Completed</th></tr></thead>
                  <tbody>
                    {fu.most_completed.map((t, i) => (
                      <tr key={i} className="border-b border-white/[0.04]"><td className="py-3 pr-3 text-neutral-500">{i + 1}</td><td className="py-3 pr-3 font-medium text-white">{t.business_name}</td><td className="py-3 text-emerald-400">{t.completed}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="fu-most-active">
              <div className="mb-5 flex items-center gap-2"><Trophy className="h-4 w-4 text-[#FF1C2A]" /><h3 className="font-display text-base font-medium text-white">Most Active Installers</h3></div>
              {fu.most_active.length === 0 ? <p className="py-6 text-center text-sm text-neutral-500">No data yet.</p> : (
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500"><th className="py-3 pr-3">#</th><th className="py-3 pr-3">Business</th><th className="py-3 pr-3">Completed</th><th className="py-3">Outstanding</th></tr></thead>
                  <tbody>
                    {fu.most_active.map((t, i) => (
                      <tr key={i} className="border-b border-white/[0.04]"><td className="py-3 pr-3 text-neutral-500">{i + 1}</td><td className="py-3 pr-3 font-medium text-white">{t.business_name}</td><td className="py-3 pr-3 text-emerald-400">{t.completed}</td><td className="py-3 text-[#FF6069]">{t.outstanding}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#141414] p-5">
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{label}</p>
      <p className={`mt-2 font-display text-3xl font-semibold ${accent ? "text-[#FF1C2A]" : "text-white"}`}>{value}</p>
    </div>
  );
}
